'use client';
import React, { ComponentProps, LegacyRef, ReactNode, forwardRef, useId, useState } from 'react';
import styles from './Input.module.scss';
import { VALIDITY } from '@/constants';
import cn from 'classnames';
import formatPhoneNumber from '@/packages/string/formatPhoneNumber';
import zipcode from '@/packages/string/zipcode';
import { InvalidFeedback } from '../InvalidFeedback/InvalidFeedback';

export type InputType = 'text' | 'email' | 'tel' | 'zip' | 'password';

export interface InputProps extends ComponentProps<'input'> {
    type: InputType;
    label: ReactNode;
    ariaLabel?: string;
    invalid?: FieldValidityState;
    invalidMessage?: ReactNode;
    dataCy?: string;
    errorDataCy?: string;
    theme?: string;
    inputWrapperStyles?: string;
    wrapperId?: string;
    noSpacing?: boolean;
}

export interface InputState {
    value: string;
    isFocused: boolean;
    isFilled: boolean;
}

const extendedTypes = ['zip'];

export const Input = forwardRef(function (
    {
        label,
        ariaLabel,
        invalid,
        invalidMessage,
        id,
        type,
        value,
        onBlur,
        onChange,
        dataCy,
        errorDataCy,
        theme,
        className,
        inputWrapperStyles,
        wrapperId,
        noSpacing,
        ...otherProps
    }: InputProps,
    ref: LegacyRef<HTMLInputElement>,
): React.JSX.Element {
    const generatedId = useId();
    const elementId = id ? id : generatedId;
    const errorId = `${elementId}-error`;
    const inputType = extendedTypes.indexOf(type) === -1 ? type : 'text';
    const ariaInvalid = invalid === VALIDITY.invalid;

    const [state, setState] = useState({
        isFocused: false,
        isFilled: Boolean(value),
        value: value || '',
    });

    const inputContainerClassNames = cn(styles.inputContainer, className, {
        [styles.active]: state.isFocused,
        [styles.filled]: state.isFilled,
        [styles.invalid]: ariaInvalid,
        [styles.emailCapture]: theme === 'emailCapture',
        [styles.noSpacing]: noSpacing,
    });

    const setIsFocused = (focused: boolean) => setState({ ...state, isFocused: focused });

    const setValue = (newValue: string) => {
        if (type === 'zip') {
            newValue = zipcode(newValue);
        }

        setState({ ...state, value: newValue, isFilled: Boolean(newValue) });
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
        setIsFocused(false);

        if (onBlur) {
            onBlur(event);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.target as HTMLInputElement;
        const newValue: string = input.value;

        if (type === 'tel') {
            setValue(formatPhoneNumber(newValue));
        } else {
            setValue(newValue);
        }

        if (onChange) {
            onChange(event);
        }
    };

    return (
        <div className={inputWrapperStyles} id={wrapperId}>
            {invalid && invalidMessage && (
                <InvalidFeedback
                    validState={invalid}
                    errorId={errorId}
                    dataCy={errorDataCy}
                    theme={theme === 'emailCapture' ? 'round' : ''}
                >
                    {invalidMessage}
                </InvalidFeedback>
            )}
            <div className={inputContainerClassNames}>
                <label htmlFor={elementId}>{label}</label>
                <input
                    {...otherProps}
                    id={elementId}
                    ref={ref}
                    aria-label={ariaLabel}
                    aria-invalid={ariaInvalid}
                    type={inputType}
                    value={state.value}
                    onFocus={() => setIsFocused(true)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    aria-describedby={errorId}
                    data-cy={dataCy}
                />
            </div>
        </div>
    );
});

Input.displayName = 'Input';
